import React, { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Search, UserPlus, Users, Mail, Phone, MapPin, Calendar, Package, DollarSign, MoreVertical, Eye, EyeOff, User, X, Trash2 } from 'lucide-react';
import BottomNavigation from '../../components/common/BottomNavigation';
import { useApp } from '../../context/AppContext';
import { useData } from '../../context/DataContext';
import { useOrder } from '../../context/OrderContext';
import { Customer } from '../../types';
import { FirestoreService } from '../../firebase/firestore';

const AdminCustomers: React.FC = () => {
  const { setCurrentScreen } = useApp();
  // const { } = useAuth(); // Removed unused destructuring
  const { customers, addCustomer, deleteCustomer } = useData();
  const { orders } = useOrder();
  
  // Debug: Log customers when they change
  useEffect(() => {
    console.log('AdminCustomers: Customers updated:', customers);
    console.log('AdminCustomers: Orders available:', orders);
  }, [customers, orders]);

  // Calculate customer totals based on actual orders
  const getCustomerTotals = (customerId: string) => {
    const customerOrders = orders.filter(order => order.customerId === customerId);
    const totalOrders = customerOrders.length;
    const totalSpent = customerOrders.reduce((sum, order) => sum + (order.amount || 0), 0);
    
    console.log(`Customer ${customerId} totals:`, { totalOrders, totalSpent, orders: customerOrders });
    
    return { totalOrders, totalSpent };
  };
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    address: '',
    city: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    show: boolean;
    customerId: string | null;
    customerInfo: string;
  }>({
    show: false,
    customerId: null,
    customerInfo: ''
  });

  // Filter customers
  const filteredCustomers = useMemo(() => {
    console.log('AdminCustomers: Filtering customers:', customers, 'searchTerm:', searchTerm);
    return customers.filter(customer =>
      customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.city.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [customers, searchTerm]);

  const handleBack = () => {
    setCurrentScreen('admin-dashboard');
  };


  const handleEditCustomer = (customer: Customer) => {
    setEditingCustomer(customer);
    setFormData({
      fullName: customer.name,
      email: customer.email,
      phone: customer.phone,
      password: '', // Don't pre-fill password for security
      address: customer.address,
      city: customer.city
    });
    setShowAddForm(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const validateForm = () => {
    if (!formData.fullName.trim()) {
      setError('Full name is required');
      return false;
    }
    if (!formData.email.trim()) {
      setError('Email is required');
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    if (!formData.phone.trim()) {
      setError('Phone number is required');
      return false;
    }
    if (formData.phone.length < 10) {
      setError('Please enter a valid phone number');
      return false;
    }
    // Password is only required for new customers, not when editing
    if (!editingCustomer && !formData.password) {
      setError('Password is required');
      return false;
    }
    if (!editingCustomer && formData.password && formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }
    if (editingCustomer && formData.password && formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }
    if (!formData.address.trim()) {
      setError('Address is required');
      return false;
    }
    if (!formData.city.trim()) {
      setError('City is required');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    setError('');

    try {
      if (editingCustomer) {
        // Edit existing customer
        const emailExists = customers.some(customer => 
          customer.email === formData.email && customer.id !== editingCustomer.id
        );
        
        if (emailExists) {
          setError('Email already exists');
          setIsLoading(false);
          return;
        }

        // Update existing customer
        const updatedCustomer: Customer = {
          ...editingCustomer,
          name: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          // Keep existing password if no new password provided
          // In a real app, you'd handle password updates differently
        };

        // Update customer in data context
        const updatedCustomers = customers.map(customer =>
          customer.id === editingCustomer.id ? updatedCustomer : customer
        );
        
        // Update localStorage
        localStorage.setItem('bookfood_customers', JSON.stringify(updatedCustomers));
        
        // Dispatch custom event to notify DataContext of the change
        window.dispatchEvent(new CustomEvent('customStorageChange'));
        
      } else {
        // Add new customer
        const emailExists = customers.some(customer => customer.email === formData.email);
        
        if (emailExists) {
          setError('Email already exists');
          setIsLoading(false);
          return;
        }

        // Create new customer
        const newCustomer: Customer = {
          id: Date.now().toString(),
          email: formData.email,
          name: formData.fullName,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          customerId: Date.now().toString(),
          profilePicture: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
          registrationDate: new Date().toISOString(),
          totalOrders: 0,
          totalSpent: 0
        };

        // Save customer to Firestore
        console.log('💾 Saving customer to Firestore...', newCustomer);
        const customerId = await FirestoreService.addCustomer(newCustomer as Omit<Customer, 'id'>);
        
        if (customerId) {
          console.log('✅ Customer saved to Firestore with ID:', customerId);
          // Add customer to data context with Firestore ID
          const customerWithId = { ...newCustomer, id: customerId };
          addCustomer(customerWithId);
        } else {
          console.error('❌ Failed to save customer to Firestore');
          setError('Failed to save customer. Please try again.');
          setIsLoading(false);
          return;
        }
      }

      // Reset form
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        password: '',
        address: '',
        city: ''
      });
      setEditingCustomer(null);
      setShowAddForm(false);
    } catch (err) {
      setError(editingCustomer ? 'Failed to update customer. Please try again.' : 'Failed to add customer. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingCustomer(null);
    setFormData({
      fullName: '',
      email: '',
      phone: '',
      password: '',
      address: '',
      city: ''
    });
    setError('');
  };

  const handleDeleteClick = (customerId: string, customerName: string, customerEmail: string) => {
    setDeleteConfirm({
      show: true,
      customerId,
      customerInfo: `${customerName} (${customerEmail})`
    });
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirm.customerId) {
      await deleteCustomer(deleteConfirm.customerId);
      setDeleteConfirm({
        show: false,
        customerId: null,
        customerInfo: ''
      });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({
      show: false,
      customerId: null,
      customerInfo: ''
    });
  };

  const CustomerCard: React.FC<{ customer: Customer; index: number }> = ({ customer, index }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className="bg-slate-800 rounded-xl p-4 border border-slate-700 hover:bg-slate-750 transition-colors duration-200"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center">
            {customer.profilePicture ? (
              <img 
                src={customer.profilePicture} 
                alt={customer.name}
                className="w-12 h-12 rounded-full object-cover"
              />
            ) : (
              <UserPlus size={24} className="text-white" />
            )}
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">{customer.name}</h3>
            <p className="text-slate-400 text-sm">{customer.email}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <motion.button
            onClick={() => handleEditCustomer(customer)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors duration-200"
            title="Edit customer"
          >
            <MoreVertical size={16} />
          </motion.button>
          <motion.button
            onClick={() => handleDeleteClick(customer.id, customer.name, customer.email)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-700 rounded-lg transition-colors duration-200"
            title="Delete customer"
          >
            <Trash2 size={16} />
          </motion.button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-3">
        <div className="flex items-center space-x-2 text-slate-300">
          <Phone size={14} />
          <span className="text-sm">{customer.phone}</span>
        </div>
        <div className="flex items-center space-x-2 text-slate-300">
          <MapPin size={14} />
          <span className="text-sm">{customer.city}</span>
        </div>
      </div>

      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-1 text-slate-400">
            <Package size={14} />
            <span>{getCustomerTotals(customer.customerId).totalOrders} orders</span>
          </div>
          <div className="flex items-center space-x-1 text-slate-400">
            <DollarSign size={14} />
            <span>₹{getCustomerTotals(customer.customerId).totalSpent}</span>
          </div>
        </div>
        <div className="flex items-center space-x-1 text-slate-500">
          <Calendar size={14} />
          <span>{new Date(customer.registrationDate).toLocaleDateString()}</span>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-slate-900 pb-20">
      {/* Header */}
      <div className="flex items-center justify-between p-6 bg-slate-800 border-b border-slate-700">
        <div className="flex items-center space-x-4">
          <motion.button
            onClick={handleBack}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors duration-200"
          >
            <ArrowLeft size={20} />
          </motion.button>
          <div>
            <h1 className="text-xl font-bold text-white">Customers</h1>
            <p className="text-slate-400 text-sm">{customers.length} total customers</p>
          </div>
        </div>
      </div>

      {/* Add Customer Form */}
      {showAddForm && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-slate-800 border-b border-slate-700 p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-white">
              {editingCustomer ? 'Edit Customer' : 'Add New Customer'}
            </h2>
            <motion.button
              onClick={handleCancel}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors duration-200"
            >
              <X size={20} />
            </motion.button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Full Name */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Full Name *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="Enter full name"
                    required
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Email *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="Enter email"
                    required
                  />
                </div>
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Phone Number *
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="Enter phone number"
                    required
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Password {!editingCustomer && '*'}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="w-full pl-4 pr-12 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder={editingCustomer ? "Leave blank to keep current password" : "Create a password"}
                    required={!editingCustomer}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {editingCustomer && (
                  <p className="text-slate-400 text-xs mt-1">
                    Leave blank to keep current password
                  </p>
                )}
              </div>
            </div>

            {/* Address */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Address *
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-3 text-slate-400" size={20} />
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                  placeholder="Enter address"
                  rows={3}
                  required
                />
              </div>
            </div>

            {/* City */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                City *
              </label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="City"
                required
              />
            </div>

            {/* Error Message */}
            {error && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-red-400 text-sm text-center"
              >
                {error}
              </motion.div>
            )}

            {/* Form Buttons */}
            <div className="flex gap-4 pt-4">
              <motion.button
                type="button"
                onClick={handleCancel}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex-1 bg-slate-600 hover:bg-slate-500 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200"
              >
                Cancel
              </motion.button>
              <motion.button
                type="submit"
                disabled={isLoading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex-1 bg-teal-500 hover:bg-teal-600 disabled:bg-slate-600 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 disabled:cursor-not-allowed"
              >
                {isLoading 
                  ? (editingCustomer ? 'Updating Customer...' : 'Adding Customer...') 
                  : (editingCustomer ? 'Update Customer' : 'Add Customer')
                }
              </motion.button>
            </div>
          </form>
        </motion.div>
      )}

      <div className="p-6 space-y-6">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
          <input
            type="text"
            placeholder="Search customers by name, email, or city..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          />
        </div>

        {/* Customers List */}
        <div className="space-y-4">
          {filteredCustomers.length > 0 ? (
            filteredCustomers.map((customer, index) => (
              <CustomerCard key={customer.id} customer={customer} index={index} />
            ))
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-12"
            >
              <div className="w-24 h-24 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users size={32} className="text-slate-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">
                {searchTerm ? 'No customers found' : 'No customers yet'}
              </h3>
              <p className="text-slate-400 mb-6">
                {searchTerm 
                  ? 'Try adjusting your search terms' 
                  : 'Customers will appear here when they sign up'
                }
              </p>
            </motion.div>
          )}
        </div>
      </div>

      <BottomNavigation />

      {/* Delete Confirmation Dialog */}
      {deleteConfirm.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-slate-800 rounded-xl p-6 max-w-md w-full border border-slate-700"
          >
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center">
                <Trash2 size={20} className="text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Delete Customer</h3>
                <p className="text-slate-400 text-sm">This action cannot be undone</p>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-slate-300 mb-2">
                Are you sure you want to delete this customer?
              </p>
              <div className="bg-slate-700 rounded-lg p-3">
                <p className="text-white font-medium">{deleteConfirm.customerInfo}</p>
              </div>
              <p className="text-slate-400 text-sm mt-2">
                This will permanently remove the customer and all their data from the system.
              </p>
            </div>
            
            <div className="flex space-x-3">
              <motion.button
                onClick={handleDeleteCancel}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex-1 bg-slate-600 hover:bg-slate-500 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
              >
                Cancel
              </motion.button>
              <motion.button
                onClick={handleDeleteConfirm}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
              >
                Delete Customer
              </motion.button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminCustomers;
